/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Material Kit 2 React examples
// import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// SEO component
import SEO from "components/SEO";

// Schema data
import { localBusinessSchema, organizationSchema, breadcrumbSchema } from "utils/schemaData";

// About Us page sections
import Information from "pages/LandingPages/MainPage/sections/Information";
// import Featuring from "pages/LandingPages/MainPage/sections/Featuring";
import History from "pages/LandingPages/MainPage/sections/History";

// Routes
// import routes from "routes";
import footerRoutes from "footer.routes";

// Images
// import bgImage from "assets/images/bg-about-us.jpg";
import bgImage from "assets/images/office-picture.jpg";
import logoImage from "assets/images/logo.png";
import ContactUs from "pages/LandingPages/MainPage/sections/ContactUs";

function MainPage() {
  const combinedSchema = [localBusinessSchema, organizationSchema, breadcrumbSchema];

  return (
    <>
      <SEO
        title="Gestoría en Granada | Asesoría Fiscal, Laboral y Contable desde 1961 — Gestoría Moreno"
        description="Gestoría Moreno en Granada: asesoría fiscal, laboral, contable y gestión administrativa. Más de 60 años de experiencia. Consulta sin compromiso. Llámanos: 958 805 110."
        keywords="gestoria granada, asesoria fiscal granada, asesoria laboral granada, asesoria contable granada, gestoria moreno, gestoria reyes catolicos granada, impuestos granada, nominas granada, contabilidad granada, gestor administrativo granada"
        canonicalUrl="/"
        ogImage="/office-picture.jpg"
        schemaData={combinedSchema}
      />
      <MKBox
        minHeight="75vh"
        width="100%"
        pb={6}
        position="relative"
        sx={{
          pt: { xs: "30vw", sm: "18vw", md: "12rem" },
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <MKBox
          position="absolute"
          top="3rem"
          width="100%"
          display="flex"
          justifyContent="center"
          sx={{ pointerEvents: "none" }}
        >
          <img
            src={logoImage}
            alt="Gestoría Moreno"
            width="180"
            height="85"
            style={{
              width: "min(600px, 75vw)",
              height: "auto",
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.4))",
              opacity: 0.8,
            }}
          />
        </MKBox>
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              Tu gestoría de confianza en Granada
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.9} mt={1}>
              Con más de <b>60 años</b> de experiencia, nos encargamos de tus impuestos, nóminas,
              contabilidad y trámites para que tú te centres en lo que importa. Más de{" "}
              <b>2.000 clientes </b>
              ya confían en nosotros.
            </MKTypography>
            <MKBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              mb={4}
              sx={{
                textAlign: "center",
                py: 2,
              }}
            >
              <MKBox display="flex" alignItems="baseline" gap={1}>
                <MKTypography
                  variant="h4"
                  component="span"
                  color="white"
                  sx={{
                    fontWeight: 300,
                    letterSpacing: "1px",
                    opacity: 0.9,
                  }}
                >
                  desde
                </MKTypography>
                <MKTypography
                  variant="h2"
                  component="span"
                  color="info"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "3rem", md: "4rem" },
                    textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    letterSpacing: "2px",
                  }}
                >
                  1961
                </MKTypography>
              </MKBox>
            </MKBox>

            <MKBox display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              <MKButton
                color="info"
                component="a"
                href="#contacto"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "25px",
                  boxShadow: "0 4px 20px rgba(243, 146, 0, 0.3)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 25px rgba(243, 146, 0, 0.4)",
                  },
                }}
              >
                Solicita tu consulta
              </MKButton>
              <MKButton
                variant="outlined"
                color="white"
                component="a"
                href="#servicios"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 500,
                  textTransform: "none",
                  borderRadius: "25px",
                  borderWidth: "2px",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    borderWidth: "2px",
                  },
                }}
              >
                Descubre nuestros servicios
              </MKButton>
            </MKBox>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* <Featuring /> */}
        <Information />
        <History />
        <ContactUs />
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default MainPage;
